import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Loader2, 
  CheckCircle, 
  MapPin, 
  Clock, 
  Users, 
  Phone,
  Mail,
  ChevronRight,
  Calendar,
  User,
  MessageSquare
} from 'lucide-react';
import { GdprConsent } from '../components/GdprConsent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// NUTS Logo especial Anyós Park (amb fons blanc per seccions clares)
const NUTS_ANYOS_LOGO = 'https://customer-assets.emergentagent.com/job_c71ccf0b-f871-4091-bddc-a51aa7862b88/artifacts/ilo445oe_NUTS%20especial%20ANYOS.png';

// Hero image - Boles UGOLF Aravell + NUTS Anyós Park
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_c71ccf0b-f871-4091-bddc-a51aa7862b88/artifacts/0a5z1mtw_boles%20aravell%20anyos%20nuts.png';

const InicicioAnyosPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const formRef = useRef(null);
  const [showPrivateForm, setShowPrivateForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Form states
  const [groupForm, setGroupForm] = useState({
    nom: '',
    telefon: '',
    email: '',
    soci_anyos: false,
    accepta_rgpd: false
  });

  const [groupMarketing, setGroupMarketing] = useState(false);
  const [privateGdpr, setPrivateGdpr] = useState({ privacy: false, marketing: false });

  const [privateForm, setPrivateForm] = useState({
    nom: '',
    telefon: '',
    email: '',
    comentaris: ''
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/iniciacio-anyos/sessions`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDate = (session) => {
    if (!session.complet) {
      setSelectedDate(session);
      setShowGroupForm(true);
      setShowPrivateForm(false);
      // Scroll to form after a short delay
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/iniciacio-anyos/inscripcio-grup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...groupForm,
          autoritza_comercial: groupMarketing,
          data_sessio: selectedDate.data
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('grup');
        setShowGroupForm(false);
        fetchSessions(); // Refresh available spots
      } else {
        setError(data.detail || 'Error en la inscripció');
      }
    } catch (err) {
      setError('Error de connexió. Torna-ho a intentar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrivateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/iniciacio-anyos/sessio-privada`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...privateForm, autoritza_comercial: privateGdpr.marketing })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('privada');
        setShowPrivateForm(false);
      } else {
        setError(data.detail || 'Error enviant la sol·licitud');
      }
    } catch (err) {
      setError('Error de connexió. Torna-ho a intentar.');
    } finally {
      setSubmitting(false);
    }
  };

  const sessionIncludes = [
    { icon: MapPin, text: 'Tour pel camp' },
    { icon: Users, text: 'Introducció al joc' },
    { icon: CheckCircle, text: 'Putt' },
    { icon: CheckCircle, text: 'Joc llarg' },
    { icon: CheckCircle, text: 'Jugar un forat' },
    { icon: CheckCircle, text: 'Buggy vídeo' },
    { icon: CheckCircle, text: 'Repte final' }
  ];

  if (success) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-[#1A4D2E] to-[#0d2617]" data-testid="iniciacio-anyos-page">
        <div className="container-custom py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl mx-auto bg-white rounded-2xl p-8 text-center shadow-2xl"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-[#1A4D2E] mb-4">
              {success === 'grup' ? 'Inscripció Confirmada!' : 'Sol·licitud Enviada!'}
            </h1>
            
            <p className="text-gray-600 mb-6">
              {success === 'grup' 
                ? "T'hem enviat un email amb tots els detalls de la teva reserva. Ens veiem al camp!"
                : "Hem rebut la teva sol·licitud. Ens posarem en contacte amb tu molt aviat per confirmar la data i hora."
              }
            </p>
            
            <div className="bg-[#F9F9F7] rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Recordatori:</strong> El pagament es realitza al camp el dia de la sessió.
              </p>
            </div>
            
            <button
              onClick={() => {
                setSuccess(null);
                setGroupForm({ nom: '', telefon: '', email: '', soci_anyos: false, accepta_rgpd: false });
                setPrivateForm({ nom: '', telefon: '', email: '', comentaris: '' });
                setSelectedDate(null);
              }}
              className="bg-[#1A4D2E] text-white px-6 py-3 rounded-lg hover:bg-[#153d24] transition-colors"
            >
              Tornar a l'inici
            </button>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white" data-testid="iniciacio-anyos-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {HERO_IMAGE && (
          <div className="w-full">
            <img 
              src={HERO_IMAGE} 
              alt="Hero" 
              className="w-full h-auto"
            />
          </div>
        )}
      </section>

      {/* Logo + Title Section - White background */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <img
              src={NUTS_ANYOS_LOGO}
              alt="NUTS especial Anyós Park"
              className="h-32 md:h-40 mx-auto mb-6"
            />
            <h1 className="font-display text-2xl md:text-4xl text-[#1A4D2E] font-bold">
              Descobreix el Golf amb el Grup especial Anyós Park
            </h1>
            <p className="text-lg md:text-xl text-[#D4AF37] font-semibold mt-2">
              Diumenges 10h
            </p>
          </motion.div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl md:text-3xl text-[#1A4D2E] font-medium mb-4">
              Què inclou la sessió?
            </h2>
            <p className="text-gray-600">Una experiència completa de 3 hores</p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {sessionIncludes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <item.icon className="w-8 h-8 text-[#1A4D2E] mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl md:text-3xl text-[#1A4D2E] font-medium mb-4">
              Preus
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Regular Price */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-6 text-center"
            >
              <p className="text-gray-600 mb-2">Públic general</p>
              <p className="text-4xl font-bold text-gray-800">50€</p>
            </motion.div>
            
            {/* Anyós Park Price */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#1A4D2E] to-[#0d2617] rounded-2xl p-6 text-center text-white relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-[#D4AF37] text-[#1A4D2E] text-xs font-bold px-3 py-1 rounded-bl-lg">
                DESCOMPTE
              </div>
              <p className="text-white/80 mb-2">Socis Anyós Park</p>
              <p className="text-4xl font-bold">40€</p>
              <p className="text-sm text-[#D4AF37] mt-2">Estalvia 10€!</p>
            </motion.div>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-6">
            * Pagament al camp el dia de la sessió
          </p>
        </div>
      </section>

      {/* Calendar / Booking Section */}
      <section className="py-16 bg-[#F9F9F7]" id="reservar">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl md:text-3xl text-[#1A4D2E] font-medium mb-4">
              Reserva la teva plaça
            </h2>
            <p className="text-gray-600">Sessions en grup els diumenges a les 10h</p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              {/* Sessions List */}
              <div className="space-y-3 mb-8">
                {sessions.map((session, index) => (
                  <motion.div
                    key={session.data}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <button
                      onClick={() => handleSelectDate(session)}
                      disabled={session.complet}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                        session.complet
                          ? 'bg-gray-100 cursor-not-allowed'
                          : selectedDate?.data === session.data
                          ? 'bg-[#1A4D2E] text-white shadow-lg'
                          : 'bg-white hover:bg-[#1A4D2E]/5 hover:shadow-md'
                      }`}
                      data-testid={`session-${session.data}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          session.complet 
                            ? 'bg-gray-200' 
                            : selectedDate?.data === session.data
                            ? 'bg-white/20'
                            : 'bg-[#1A4D2E]/10'
                        }`}>
                          <Calendar className={`w-5 h-5 ${
                            session.complet 
                              ? 'text-gray-400' 
                              : selectedDate?.data === session.data
                              ? 'text-white'
                              : 'text-[#1A4D2E]'
                          }`} />
                        </div>
                        <div className="text-left">
                          <p className={`font-semibold ${
                            session.complet ? 'text-gray-400' : ''
                          }`}>
                            {session.data_display}
                          </p>
                          <p className={`text-sm ${
                            session.complet 
                              ? 'text-gray-400' 
                              : selectedDate?.data === session.data
                              ? 'text-white/70'
                              : 'text-gray-500'
                          }`}>
                            10:00h
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {session.complet ? (
                          <span className="bg-red-100 text-red-600 text-xs font-semibold px-3 py-1 rounded-full">
                            COMPLET
                          </span>
                        ) : (
                          <>
                            <span className={`text-sm ${
                              selectedDate?.data === session.data ? 'text-white/70' : 'text-gray-500'
                            }`}>
                              {session.places_disponibles} places
                            </span>
                            <ChevronRight className={`w-5 h-5 ${
                              selectedDate?.data === session.data ? 'text-white' : 'text-gray-400'
                            }`} />
                          </>
                        )}
                      </div>
                    </button>
                  </motion.div>
                ))}
                
                {sessions.length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    No hi ha sessions disponibles en aquest moment.
                  </p>
                )}
              </div>

              {/* Group Registration Form */}
              {showGroupForm && selectedDate && (
                <motion.div
                  ref={formRef}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-6 shadow-lg"
                >
                  <h3 className="font-display text-xl text-[#1A4D2E] font-medium mb-4">
                    Inscripció per al {selectedDate.data_display}
                  </h3>
                  
                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                      {error}
                    </div>
                  )}
                  
                  <form onSubmit={handleGroupSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="w-4 h-4 inline mr-1" />
                        Nom i cognoms *
                      </label>
                      <input
                        type="text"
                        required
                        value={groupForm.nom}
                        onChange={(e) => setGroupForm({ ...groupForm, nom: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="El teu nom complet"
                        data-testid="group-nom"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Telèfon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={groupForm.telefon}
                        onChange={(e) => setGroupForm({ ...groupForm, telefon: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="+34 600 000 000"
                        data-testid="group-telefon"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={groupForm.email}
                        onChange={(e) => setGroupForm({ ...groupForm, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="el.teu@email.com"
                        data-testid="group-email"
                      />
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-[#1A4D2E]/5 rounded-lg">
                      <input
                        type="checkbox"
                        id="soci_anyos"
                        checked={groupForm.soci_anyos}
                        onChange={(e) => setGroupForm({ ...groupForm, soci_anyos: e.target.checked })}
                        className="mt-1 w-5 h-5 text-[#1A4D2E] rounded focus:ring-[#1A4D2E]"
                        data-testid="group-soci"
                      />
                      <label htmlFor="soci_anyos" className="text-sm">
                        <span className="font-semibold text-[#1A4D2E]">Sóc soci d'Anyós Park</span>
                        <br />
                        <span className="text-gray-600">Marca aquesta casella per aplicar el preu especial de 40€</span>
                      </label>
                    </div>
                    
                    <GdprConsent
                      privacy={groupForm.accepta_rgpd}
                      onPrivacyChange={(checked) => setGroupForm({ ...groupForm, accepta_rgpd: checked })}
                      marketing={groupMarketing}
                      onMarketingChange={setGroupMarketing}
                      showMarketing
                      idPrefix="group"
                    />
                    
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#1A4D2E] text-white py-4 rounded-lg font-semibold hover:bg-[#153d24] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="group-submit"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Confirmar Inscripció
                        </>
                      )}
                    </button>
                  </form>
                </motion.div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-px bg-gray-300" />
                <span className="text-gray-500 text-sm">o bé</span>
                <div className="flex-1 h-px bg-gray-300" />
              </div>

              {/* Private Session Button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => {
                  setShowPrivateForm(true);
                  setShowGroupForm(false);
                  setSelectedDate(null);
                }}
                className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  showPrivateForm
                    ? 'bg-[#D4AF37] text-white'
                    : 'bg-white border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white'
                }`}
                data-testid="private-session-btn"
              >
                <User className="w-5 h-5" />
                Sol·licitar sessió privada
              </motion.button>

              {/* Private Session Form */}
              {showPrivateForm && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-6 shadow-lg mt-6"
                >
                  <h3 className="font-display text-xl text-[#1A4D2E] font-medium mb-2">
                    Sol·licitar Sessió Privada
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Indica'ns quines dates i hores et podrien anar bé i ens posarem en contacte amb tu.
                  </p>
                  
                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                      {error}
                    </div>
                  )}
                  
                  <form onSubmit={handlePrivateSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="w-4 h-4 inline mr-1" />
                        Nom i cognoms *
                      </label>
                      <input
                        type="text"
                        required
                        value={privateForm.nom}
                        onChange={(e) => setPrivateForm({ ...privateForm, nom: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="El teu nom complet"
                        data-testid="private-nom"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Telèfon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={privateForm.telefon}
                        onChange={(e) => setPrivateForm({ ...privateForm, telefon: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="+34 600 000 000"
                        data-testid="private-telefon"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={privateForm.email}
                        onChange={(e) => setPrivateForm({ ...privateForm, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="el.teu@email.com"
                        data-testid="private-email"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <MessageSquare className="w-4 h-4 inline mr-1" />
                        Disponibilitat / Comentaris *
                      </label>
                      <textarea
                        required
                        value={privateForm.comentaris}
                        onChange={(e) => setPrivateForm({ ...privateForm, comentaris: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        rows={4}
                        placeholder="Indica'ns quines dates i hores et podrien anar bé..."
                        data-testid="private-comentaris"
                      />
                    </div>
                    
                    <GdprConsent
                      privacy={privateGdpr.privacy}
                      onPrivacyChange={(checked) => setPrivateGdpr(prev => ({ ...prev, privacy: checked }))}
                      marketing={privateGdpr.marketing}
                      onMarketingChange={(checked) => setPrivateGdpr(prev => ({ ...prev, marketing: checked }))}
                      showMarketing
                      idPrefix="private"
                    />
                    
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#D4AF37] text-white py-4 rounded-lg font-semibold hover:bg-[#b8952e] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="private-submit"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          Enviar Sol·licitud
                        </>
                      )}
                    </button>
                  </form>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-[#1A4D2E] text-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-2xl font-medium mb-4">
                On som?
              </h2>
              <div className="space-y-3">
                <p className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  Av. del Cadí 6, 25712 Aravell, Lleida
                </p>
                <p className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#D4AF37]" />
                  +34 661 19 40 23
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                  secretaria@aravellgolfclub.com
                </p>
                <p className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  Sessions els diumenges a les 10:00h
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="aspect-video rounded-xl overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.1673789853567!2d1.5680611!3d42.3897222!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a5f1a2a2a2a2a2%3A0x1234567890abcdef!2sUGOLF%20ARAVELL%20ANDORRA!5e0!3m2!1sca!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicació UGOLF ARAVELL"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default InicicioAnyosPage;
